import { DndContext, useDroppable } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import { RecycleDialog } from 'components/Posts/components/RecycleSelector'
import { ScrollSeekPlaceholder } from 'components/VirtualizedList'
import { useTikTokDataFetch } from 'hooks/useTikTokDataFetch'
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { ScrollSeekPlaceholderProps, Virtuoso } from 'react-virtuoso'
import {
  applyGenericStatusToPosts,
  initializeTikTokSettings,
  removeMultiPosts,
  reorderMultiPosts,
  setMultiPostNetworkSettings,
  setMultiPostsRecycle
} from 'services/compose'
import {
  composerBulkPostsSelector,
  composerSelectedProfilesSelector,
  multiPostsCountSelector,
  useGenericStatusSelector,
  visibleMultiPostsSelector
} from 'services/compose/selectors'
import { BulkContentPost } from 'services/compose/state'
import { getPinterestBoards, pinterestBoardsByPpidSelector } from 'services/destinations'
import { BRAND_TIKTOK, TikTokPostProfileSettings, BRAND_PINTEREST, PostDestination } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import { SortableBulkPost } from './BulkPost'
import postStyles from './BulkPost.pcss'
import styles from './BulkPostsManager.pcss'
import { resetFetchedArticleUrls } from './hooks/useArticleLinkDetails'
import { useBulkPostHandlers } from './hooks/useBulkPostHandlers'
import { useBulkSelection } from './hooks/useBulkSelection'
import { MediaPreviewPopup } from './MediaPreviewPopup'
import MultiPostBulkActions from './MultiPostBulkActions'
import { NetworkOptionsPopup, NetworkOptionsSelection } from './NetworkOptionsPopup'
import { applyDragEnd, mergeVisibleOrder } from './postOrder'

const deepEqual = require('react-fast-compare')

const EMPTY_PROFILES: PostDestination[] = []
const EMPTY_BOARDS = {}

export const BulkPostsManager = memo(() => {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [linkedPostsCaptions, setLinkedPostsCaption] = useState<Record<string, boolean>>({})
  const [recycleDialogOpen, setRecycleDialogOpen] = useState(false)
  const [filePreview, setFilePreview] = useState<{ url: string, selector?: string, type: 'image' | 'video' } | null>(null)
  const posts: BulkContentPost[] = useSelector(visibleMultiPostsSelector)
  const allPosts: BulkContentPost[] = useSelector(composerBulkPostsSelector)
  const count = useSelector(multiPostsCountSelector, shallowEqual)
  const boards = useSelector(pinterestBoardsByPpidSelector, shallowEqual)
  const selectedProfiles: PostDestination[] = useSelector(composerSelectedProfilesSelector, deepEqual)
  const tkIds = useMemo(() => selectedProfiles.filter(p => p.type === BRAND_TIKTOK).map(p => p.ppid), [selectedProfiles])
  const profilesByNetwork = useMemo(() => {
    return selectedProfiles.reduce((map: { [network: string]: PostDestination[] }, profile) => {
      if (map[profile.type]) {
        map[profile.type].push(profile)
      } else {
        map[profile.type] = [profile]
      }
      return map
    }, {})
  }, [selectedProfiles])
  const [tiktokData] = useTikTokDataFetch(tkIds.join(','))
  const isUsingGenericStatus = useSelector(useGenericStatusSelector)
  const [networkOptionsPopupOpen, setNetworkOptionsPopupOpen] = useState(false)

  const {
    selectedPostIds,
    postsMap,
    allSelected,
    toggleSelected,
    toggleAllSelected,
    removeFromSelection,
    clearSelection
  } = useBulkSelection(posts)

  const orderRef = useRef<string[]>([])
  const pinterestBoardsFetched = useRef<Record<string, boolean>>({})
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setScrollElement(document.querySelector('[data-testid="composer"]') as HTMLElement)
  }, [])

  // The fetched article urls are cached at module level so they survive row virtualization -
  // drop them when the bulk composer goes away.
  useEffect(() => resetFetchedArticleUrls, [])

  useEffect(() => {
    profilesByNetwork[BRAND_PINTEREST]?.forEach(profile => {
      if (!pinterestBoardsFetched.current[profile.ppid]) {
        dispatch(getPinterestBoards({ profile }))
        pinterestBoardsFetched.current[profile.ppid] = true
      }
    })
  }, [profilesByNetwork, dispatch])

  useEffect(() => {
    const ppids = Object.keys(tiktokData)
    const tiktokSettings = ppids.reduce((acc, ppid: string) => {
      acc[ppid] = tiktokData[ppid].settings
      return acc
    }, {} as Record<string, TikTokPostProfileSettings>)
    dispatch(initializeTikTokSettings(tiktokSettings))
  }, [tiktokData, dispatch])

  const order = useMemo(() => {
    const next = posts.map(p => p.id)
    orderRef.current = next
    return next
  }, [posts])

  // Assigned during render (not in an effect) so a reorder dispatched in the same commit
  // never merges against a stale/empty full order.
  const allIds = useMemo(() => allPosts.map(p => p.id), [allPosts])
  const allIdsRef = useRef(allIds)
  allIdsRef.current = allIds

  // Reorder actions operate on the visible (network-filtered) list; expand to the full list so
  // posts of deselected networks keep their positions instead of ending up with clashing order values.
  const reorderVisible = useCallback((nextVisibleIds: string[]) => {
    dispatch(reorderMultiPosts(mergeVisibleOrder(allIdsRef.current, nextVisibleIds)))
  }, [dispatch])

  const markCaptionUnlinked = useCallback((postId: string) => {
    setLinkedPostsCaption(current => {
      if (current[postId]) {
        return { ...current, [postId]: false }
      }
      return current
    })
  }, [])

  const handlers = useBulkPostHandlers({
    orderRef,
    reorderVisible,
    toggleSelected,
    onPostRemoved: removeFromSelection,
    onStatusEdited: markCaptionUnlinked,
    onMediaClick: setFilePreview
  })

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: 'droppable'
  })

  const openRecycleDialog = () => {
    setRecycleDialogOpen(true)
  }

  const closeRecycleDialog = () => {
    setRecycleDialogOpen(false)
  }

  const onBulkRecycle = (value: boolean | Date | number) => {
    dispatch(setMultiPostsRecycle({ postIds: selectedPostIds, value }))
    closeRecycleDialog()
  }

  const onDragEnd = (event: any) => {
    const nextVisible = applyDragEnd(order, event.active.id, event.over?.id)
    if (nextVisible) {
      reorderVisible(nextVisible)
    }
  }

  const bulkDeletePosts = () => {
    // Only visible (network-filtered) posts can be selected — never wipe the whole multiPost map.
    dispatch(removeMultiPosts({ ids: selectedPostIds }))
    clearSelection()
  }

  const applyGenericStatus = () => {
    dispatch(applyGenericStatusToPosts(selectedPostIds))
    setLinkedPostsCaption(current => ({
      ...current,
      ...selectedPostIds.reduce((map: Record<string, boolean>, id) => {
        map[id] = true
        return map
      }, {})
    }))
    clearSelection()
  }

  const openNetworkSettingsDialog = () => {
    setNetworkOptionsPopupOpen(true)
  }

  const closeSettingsPopup = () => {
    setNetworkOptionsPopupOpen(false)
  }

  const onBulkUpdateNetworkSettings = (selection: NetworkOptionsSelection) => {
    dispatch(setMultiPostNetworkSettings({
      postIds: selectedPostIds,
      ...selection
    }))
    closeSettingsPopup()
  }

  const closeMediaPreviewPopup = () => {
    setFilePreview(null)
  }

  return (
    <Fragment>
      <DndContext onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
        <SortableContext items={order} strategy={rectSortingStrategy}>
          <div className={styles.container} ref={setDroppableNodeRef}>
            <div className={styles.header}>
              <div className={styles.left}>
                <div className={styles.count}>
                  <FormattedMessage id="composer.multi-post.labels.count" values={{ count }} />
                </div>
              </div>
              {posts.length > 1 && (
                <div className={styles.right}>
                  <MultiPostBulkActions
                    disabled={selectedPostIds.length === 0}
                    selectedPostsCount={selectedPostIds.length}
                    allSelected={allSelected}
                    onRecycle={openRecycleDialog}
                    onDelete={bulkDeletePosts}
                    onToggleAllSelected={toggleAllSelected}
                    onApplyGenericStatus={applyGenericStatus}
                    onEditNetworkSettings={openNetworkSettingsDialog}
                  />
                </div>
              )}
            </div>
            <div className={styles.list}>
              <Virtuoso
                customScrollParent={scrollElement || undefined}
                increaseViewportBy={700}
                overscan={700}
                totalCount={posts.length}
                components={{ ScrollSeekPlaceholder: BulkPostPlaceholder }}
                scrollSeekConfiguration={{
                  /* eslint-disable no-magic-numbers */
                  enter: (velocity) => Math.abs(velocity) > 800,
                  exit: (velocity) => Math.abs(velocity) < 80
                  /* eslint-enable no-magic-numbers */
                }}
                computeItemKey={index => posts[index]?.id ?? index}
                itemContent={index => {
                  // Virtuoso can render an index from the previous list while `posts` is shrinking.
                  const post = posts[index]
                  if (!post) {
                    return null
                  }
                  return (
                    <SortableBulkPost
                      key={post.id}
                      post={post}
                      profiles={profilesByNetwork[post.network] || EMPTY_PROFILES}
                      tabIndex={index + 1}
                      tiktokData={post.network === BRAND_TIKTOK ? tiktokData : undefined}
                      selected={selectedPostIds.includes(post.id)}
                      boards={post.network === BRAND_PINTEREST ? boards : EMPTY_BOARDS}
                      allowStatusOverride={Boolean(isUsingGenericStatus || linkedPostsCaptions[post.id])}
                      handlers={handlers}
                    />
                  )
                }}
              />
            </div>
          </div>
        </SortableContext>
      </DndContext>
      <RecycleDialog open={recycleDialogOpen} onClose={closeRecycleDialog} onSubmit={onBulkRecycle} />
      {networkOptionsPopupOpen && (
        <NetworkOptionsPopup
          open
          posts={selectedPostIds.map(id => postsMap[id]).filter(Boolean)}
          profiles={profilesByNetwork}
          onCancel={closeSettingsPopup}
          onConfirm={onBulkUpdateNetworkSettings}
        />
      )}
      {filePreview && (
        <MediaPreviewPopup
          fileUrl={filePreview.url}
          mediaType={filePreview.type}
          elementSelector={filePreview.selector}
          onClose={closeMediaPreviewPopup}
        />
      )}
    </Fragment>
  )
})

function BulkPostPlaceholder(props: ScrollSeekPlaceholderProps) {
  return <ScrollSeekPlaceholder {...props} containerClassName={postStyles.outerContainer} className={postStyles.placeholder} />
}

export default BulkPostsManager
